// CustomDropdown.tsx
import { ChevronRight } from "lucide-react";

interface DropdownOption<T = string> {
  value: T;
  label: string;
}

interface CustomDropdownProps<T = string> {
  label: string;
  value: T;
  options: DropdownOption<T>[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSelect: (value: T) => void;
  isEditing: boolean;
  dropdownRef: React.RefObject<HTMLDivElement>;
}

const CustomDropdown = <T extends string | boolean = string>({
  label,
  value,
  options,
  isOpen,
  setIsOpen,
  onSelect,
  isEditing,
  dropdownRef,
}: CustomDropdownProps<T>) => {
  // Find the label for the current value
  const currentLabel =
    options.find((option) => option.value === value)?.label || String(value);

  return (
    <div>
      <label className="text-sm text-gray-500">{label}</label>
      {isEditing ? (
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full p-2 border rounded-md mt-1 text-left flex justify-between items-center"
          >
            <span>{currentLabel}</span>
            <ChevronRight
              size={16}
              className={`transform transition-transform ${isOpen ? "rotate-90" : ""}`}
            />
          </button>
          {isOpen && (
            <div className="absolute left-0 mt-2 w-full bg-white rounded-md shadow-lg z-10 border">
              {options.map((option) => (
                <button
                  key={String(option.value)}
                  onClick={() => {
                    onSelect(option.value);
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="font-medium text-gray-800 capitalize">{currentLabel}</p>
      )}
    </div>
  );
};

export default CustomDropdown;
